import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Stack,
  Typography,
  useTheme,
  Divider,
  LinearProgress,
} from '@mui/material';
import { useForm, FormProvider } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonAddIcon from '@mui/icons-material/PersonAdd';

import { sendEmailToVerifyEmail, signUp } from '../../../redux/auth/authSlice';
import { API_CALL_STATUS } from '../../../assets/constants/constants';
import Loader from '../../loader/Loader';
import { useNotification } from '../../shared/NotificationProvider';
import OAuth from '../shared/OAuth/OAuth';
import FormInput from '../shared/FormInput';
import EmailVerification from './components/EmailVerification';

const SignUpForm = () => {
  const theme = useTheme();
  const [openModal, setOpenModal] = useState(false);
  const [emailToBeVerified, setEmailToBeVerified] = useState("");
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordSuggestions, setPasswordSuggestions] = useState([]);

  const otpVerificationRes = useSelector(state => state.auth.otpVerificationRes);
  const verifyEmailRes = useSelector(state => state.auth.verifyEmailRes);
  const apiCallStatus = useSelector(state => state.auth.authStatus);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const notify = useNotification();

  const methods = useForm({ mode: "onChange" });
  const { handleSubmit, formState: { isDirty, isValid, isSubmitting }, watch } = methods;

  const password = watch('password', '');

  // Password strength checker
  useEffect(() => {
    if (!password) {
      setPasswordStrength(0);
      setPasswordSuggestions([]);
      return;
    }

    let strength = 0;
    const suggestions = [];

    // Check criteria
    if (password.length >= 8) strength += 25;
    else suggestions.push('At least 8 characters');

    if (/[A-Z]/.test(password) && /[a-z]/.test(password)) strength += 25;
    else suggestions.push('Mix of uppercase and lowercase letters');

    if (/\d/.test(password)) strength += 25;
    else suggestions.push('Include at least one number');

    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) strength += 25;
    else suggestions.push('Include at least one special character');

    setPasswordStrength(strength);
    setPasswordSuggestions(suggestions);
  }, [password]);

  const getPasswordStrengthColor = () => {
    if (passwordStrength >= 75) return '#00ff88';
    if (passwordStrength >= 50) return '#ffcc00';
    if (passwordStrength >= 25) return '#ff9900';
    return '#ff3366';
  };

  const handleSignUp = async (formData) => {
    const { username, email, password } = formData;
    
    // Check if email is verified
    const isEmailVerified = otpVerificationRes?.status === "success" || verifyEmailRes?.status === "idle";
    if (!isEmailVerified && email === emailToBeVerified) {
      notify.warning('Please verify your email before signing up.');
      return;
    }

    try {
      const res = await dispatch(signUp({ name: username, email, password }));
      if (res.payload?.status === 'success') {
        methods.reset();
        notify.success('Account created successfully! Redirecting...');
        setTimeout(() => {
          navigate("/fmr/posts");
        }, 2000);
      } else {
        notify.error(res.payload?.message || "Sign up failed. Please try again.");
      }
    } catch {
      notify.error("System error occurred");
    }
  };

  const handleVerifyEmail = async () => {
    if (!emailToBeVerified) {
      notify.error("Please enter a valid email");
      return;
    }

    // Validate email format
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    if (!emailRegex.test(emailToBeVerified)) {
      notify.error("Please enter a valid email address");
      return;
    }

    try {
      const res = await dispatch(sendEmailToVerifyEmail({ email: emailToBeVerified }));
      if (res.payload.status === 'success') {
        setOpenModal(true);
        notify.success('Verification email sent! Check your inbox.');
      } else if (res.payload.status === 'idle') {
        notify.success(res.payload.message);
      } else {
        notify.error(res.payload.message || "Failed to send verification email");
      }
    } catch {
      notify.error("System error occurred");
    }
  };

  const isEmailVerified = otpVerificationRes?.status === "success" || verifyEmailRes?.status === "idle";

  return (
    <FormProvider {...methods}>
      {apiCallStatus === API_CALL_STATUS.LOADING && <Loader />}

      <Box 
        component="form" 
        onSubmit={handleSubmit(handleSignUp)} 
        sx={{ 
          mt: 3,
          width: '100%',
        }}
      >
        <Stack spacing={3}>
          {/* Full Name Field */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
          >
            <FormInput
              name="username"
              label="Full Name"
              placeholder="John Doe"
              startIcon={<PersonIcon sx={{ color: 'rgba(255,255,255,0.6)' }} />}
              validationRules={{
                minLength: {
                  value: 2,
                  message: "Name must be at least 2 characters"
                }
              }}
            />
          </motion.div>

          {/* Email Field with Verification */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <FormInput
              name="email"
              label="Email Address"
              type="email"
              defaultValue={emailToBeVerified || verifyEmailRes?.data || ""}
              onChange={(e) => setEmailToBeVerified(e.target.value)}
              placeholder="you@example.com"
              startIcon={<EmailIcon sx={{ color: 'rgba(255,255,255,0.6)' }} />}
              endIcon={
                <EmailVerification
                  isVerified={isEmailVerified}
                  onVerifyClick={handleVerifyEmail}
                  openModal={openModal}
                  setOpenModal={setOpenModal}
                />
              }
            />
            
            {isEmailVerified && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                style={{ display: 'flex', alignItems: 'center', gap: 1, marginTop: 1 }}
              >
                <CheckCircleIcon sx={{ color: '#00ff88', fontSize: '1rem' }} />
                <Typography variant="caption" sx={{ color: '#00ff88' }}>
                  Email verified successfully!
                </Typography>
              </motion.div>
            )}
          </motion.div>

          {/* Password Field with Strength Meter */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
          >
            <FormInput
              name="password"
              label="Password"
              type="password"
              placeholder="Create a strong password"
              startIcon={<LockIcon sx={{ color: 'rgba(255,255,255,0.6)' }} />}
              showPasswordToggle={true}
            />
            {/* Password Strength Meter */}
            {password && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                transition={{ duration: 0.3 }}
                style={{ marginTop: 1 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    Password strength:
                  </Typography>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      fontWeight: 600,
                      color: getPasswordStrengthColor(),
                    }}
                  >
                    {passwordStrength >= 75 ? 'Strong' : 
                      passwordStrength >= 50 ? 'Medium' : 
                        passwordStrength >= 25 ? 'Weak' : 'Very Weak'}
                  </Typography>
                </Box>

                <LinearProgress 
                  variant="determinate" 
                  value={passwordStrength} 
                  sx={{
                    height: 4,
                    borderRadius: 2,
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: getPasswordStrengthColor(),
                      borderRadius: 2,
                    },
                  }}
                />

                {/* Password Suggestions */}
                {passwordSuggestions.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                      Suggestions to improve:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, m: 0.5 }}>
                      {passwordSuggestions.map((suggestion, index) => (
                        <motion.li
                          key={index}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                        >
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                            {suggestion}
                          </Typography>
                        </motion.li>
                      ))}
                    </Box>
                  </Box>
                )}
              </motion.div>
            )}
          </motion.div>

          {/* Terms and Conditions */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', lineHeight: 1.6 }}>
              By creating an account, you agree to our{' '}
              <a href="/terms" style={{ color: '#00ff88', textDecoration: 'none' }}>
                Terms of Service
              </a>{' '}
              and{' '}
              <a href="/privacy" style={{ color: '#00ff88', textDecoration: 'none' }}>
                Privacy Policy
              </a>
              .
            </Typography>
          </motion.div>

          {/* Create Account Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Button
              variant="gradient"
              type="submit"
              disabled={isSubmitting || apiCallStatus === 'loading'}
              fullWidth
              startIcon={<PersonAddIcon />}
              sx={{
                py: 1.8,
                fontSize: '1rem',
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: '12px',
                background: 'linear-gradient(45deg, #00ff88, #0099ff)',
                color: '#000814',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(45deg, transparent, rgba(255,255,255,0.2), transparent)',
                  transform: 'translateX(-100%)',
                  transition: 'transform 0.6s ease',
                },
                '&:hover::before': {
                  transform: 'translateX(100%)',
                },
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 30px rgba(0, 255, 136, 0.4)',
                },
                '&:disabled': {
                  background: 'rgba(255,255,255,0.1)',
                  color: 'rgba(255,255,255,0.5)',
                },
              }}
            >
              {isSubmitting || apiCallStatus === 'loading' ? 'Creating Account...' : 'Create Account'}
            </Button>
          </motion.div>

          {/* Divider */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            <Divider 
              sx={{ 
                my: 1,
                '&::before, &::after': {
                  borderColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              <Typography 
                variant="body2" 
                sx={{
                  color: 'rgba(255, 255, 255, 0.6)',
                  fontWeight: 600,
                  letterSpacing: '0.05em',
                  px: 2,
                  background: 'linear-gradient(135deg, rgba(0, 29, 61, 0.8) 0%, rgba(0, 8, 20, 0.9) 100%)',
                }}
              >
                OR CONTINUE WITH
              </Typography>
            </Divider>
          </motion.div>

          {/* OAuth */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
          >
            <OAuth />
          </motion.div>
        </Stack>
      </Box>
    </FormProvider>
  );
};

export default SignUpForm;